import { useEffect, useId, useRef, useState } from 'react'
import { Dialog } from './Dialog'
import { PickerFoot } from './PickerFoot'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
import { initialsOf } from '../lib/initials'
import { searchMentionable, type MentionablePerson } from '../lib/mentions'
import { spot, suggestPhotoTag, type TaggedPerson, type TagSuggestion } from '../lib/photoTags'

/** The server's ceiling in set_photo_tags (143). */
export const MAX_TAGS = 20

/**
 * 161. A tag sits ON the person in the photo.
 *
 * David, 11 Oct: "the UI on tag someone is pretty bad ... Can we tag people
 * directly in the photo with the tag pinned to them in the photo?" The 143
 * tagger was a search box and a row of chips under a thumbnail, and in a kept
 * photo's row it fell into a grid built for three columns: caption squeezed to
 * 64px, chips over the opt-out line, Remove on a row of its own.
 *
 * ── HOW IT WORKS ───────────────────────────────────────────────────────────
 *
 *   * Tap a face → a dot appears there and "Who is this?" opens beside it.
 *     Choose a name and the tag is pinned to that spot.
 *   * Drag a pin to move it. Tap it to select it; the × removes it.
 *   * "Place" / "Move" on a row of the list puts that person where the next
 *     tap lands.
 *   * Keyboard: "Or add someone by name" adds without a place; Place from the
 *     keyboard drops the pin in the middle and focuses it; the arrow keys move
 *     a focused pin (Shift for bigger steps) and Delete removes it.
 *
 * A spot is a fraction of the photo (0..1 from the top-left), so it means the
 * same at every display size; the feed copy has EXIF orientation baked in.
 * A tag with no spot is still a tag (161's header).
 *
 * Nothing is written here. Done hands the list back; the composer saves it
 * after the post saves, as 143 always has, because the server needs the post
 * to know who may see the tag.
 *
 * It is a Dialog over the composer's Dialog. Escape clears the open search,
 * the "place" mode or the selection first, and only then closes.
 */
export function PhotoTagEditor({
  src,
  width,
  height,
  title,
  value,
  onDone,
  onClose,
}: {
  /** The photo to tag on: an object URL for a new photo, a signed URL for a
   *  kept one, null while that is still being signed. */
  src: string | null
  width: number
  height: number
  title: string
  value: TaggedPerson[]
  onDone: (next: TaggedPerson[]) => void
  onClose: () => void
}) {
  const t = COMPOSE[useHubLang()]
  const [tags, setTags] = useState<TaggedPerson[]>(value)
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null)
  const [placing, setPlacing] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [hover, setHover] = useState<string | null>(null)
  const [focusPin, setFocusPin] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const layerRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ id: string; sx: number; sy: number; moved: boolean; pid: number } | null>(null)

  const full = tags.length >= MAX_TAGS
  const exclude = new Set(tags.map((p) => p.personId))
  const placingName = placing ? tags.find((p) => p.personId === placing)?.displayName ?? null : null

  // Focus a pin once it exists in the DOM (keyboard Place).
  useEffect(() => {
    if (!focusPin) return
    const el = layerRef.current?.querySelector<HTMLButtonElement>(`[data-pin="${focusPin}"]`)
    el?.focus()
    setFocusPin(null)
  }, [focusPin, tags])

  function pointFrom(e: { clientX: number; clientY: number }) {
    const r = layerRef.current?.getBoundingClientRect()
    if (!r || r.width === 0 || r.height === 0) return { x: 0.5, y: 0.5 }
    return { x: spot((e.clientX - r.left) / r.width), y: spot((e.clientY - r.top) / r.height) }
  }

  function moveTo(id: string, p: { x: number; y: number }) {
    setTags((cur) => cur.map((x) => (x.personId === id ? { ...x, x: p.x, y: p.y } : x)))
  }

  function remove(id: string) {
    setTags((cur) => cur.filter((x) => x.personId !== id))
    if (selected === id) setSelected(null)
    if (placing === id) setPlacing(null)
  }

  function add(person: MentionablePerson, at: { x: number; y: number } | null) {
    if (full || exclude.has(person.personId)) return
    setTags((cur) => [...cur, { ...person, x: at?.x ?? null, y: at?.y ?? null }])
    setPending(null)
  }

  function onLayerClick(e: React.MouseEvent) {
    // Pins and the search are children; their clicks are theirs.
    if (e.target !== e.currentTarget) return
    const p = pointFrom(e)
    if (placing) {
      moveTo(placing, p)
      setSelected(placing)
      setPlacing(null)
      return
    }
    setSelected(null)
    if (full) return
    setPending(p)
  }

  function onPinDown(e: React.PointerEvent, id: string) {
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('.tagpin__x')) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag.current = { id, sx: e.clientX, sy: e.clientY, moved: false, pid: e.pointerId }
  }
  function onPinMove(e: React.PointerEvent) {
    const d = drag.current
    if (!d || d.pid !== e.pointerId) return
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 4) return
    d.moved = true
    moveTo(d.id, pointFrom(e))
  }
  function onPinUp(e: React.PointerEvent) {
    const d = drag.current
    if (!d || d.pid !== e.pointerId) return
    drag.current = null
    setPending(null)
    if (d.moved) setSelected(d.id)
    else setSelected((s) => (s === d.id ? null : d.id))
  }

  function onPinKey(e: React.KeyboardEvent, p: TaggedPerson) {
    if (p.x === null || p.y === null) return
    const step = e.shiftKey ? 0.05 : 0.01
    const d: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
    }
    if (d[e.key]) {
      e.preventDefault()
      moveTo(p.personId, { x: spot(p.x + d[e.key][0]), y: spot(p.y + d[e.key][1]) })
      setSelected(p.personId)
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      remove(p.personId)
    }
  }

  function startPlacing(id: string, fromKeyboard: boolean) {
    setPending(null)
    if (fromKeyboard) {
      // No pointer to aim with: drop it in the middle and hand over the arrows.
      const cur = tags.find((x) => x.personId === id)
      if (cur && (cur.x === null || cur.y === null)) moveTo(id, { x: 0.5, y: 0.5 })
      setSelected(id)
      setPlacing(null)
      setFocusPin(id)
      return
    }
    setSelected(null)
    setPlacing((cur) => (cur === id ? null : id))
  }

  // Escape steps back one thing at a time before it closes the dialog.
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Escape') return
    if (pending || placing || selected) {
      e.preventDefault()
      e.stopPropagation()
      setPending(null)
      setPlacing(null)
      setSelected(null)
    }
  }

  const placed = tags.filter((p) => p.x !== null && p.y !== null)

  return (
    <Dialog open title={title} onClose={onClose} className="tageditor">
      <div className="tageditor__body" onKeyDown={onKeyDown}>
        <div className="tageditor__main">
          <p className="tageditor__hint" role="status">
            {placingName ? t.tagPlaceHint(placingName) : full ? t.tagLimit : t.tagHint}
            {placingName && (
              <button type="button" className="btn btn--link btn--xs" onClick={() => setPlacing(null)}>
                {t.cancel}
              </button>
            )}
          </p>
          <div className="tageditor__stage">
            <div className="tageditor__frame">
              {src && !failed ? (
                <img
                  className="tageditor__img"
                  src={src}
                  alt=""
                  width={width}
                  height={height}
                  draggable={false}
                  onError={() => setFailed(true)}
                />
              ) : (
                <div className="tageditor__holder" style={{ aspectRatio: `${width} / ${height}` }}>
                  {failed ? t.photoFailed : t.tagPhotoLoading}
                </div>
              )}
              <div
                ref={layerRef}
                className={`tageditor__layer${placing ? ' is-placing' : ''}${full && !placing ? ' is-full' : ''}`}
                onClick={onLayerClick}
              >
                {placed.map((p) => {
                  const isSel = selected === p.personId
                  return (
                    <div
                      key={p.personId}
                      className={`tagpin ${pinPlace(p.x as number, p.y as number)}${isSel ? ' is-selected' : ''}${hover === p.personId ? ' is-hover' : ''}`}
                      style={{ left: `${(p.x as number) * 100}%`, top: `${(p.y as number) * 100}%` }}
                      onPointerDown={(e) => onPinDown(e, p.personId)}
                      onPointerMove={onPinMove}
                      onPointerUp={onPinUp}
                      onPointerCancel={() => (drag.current = null)}
                    >
                      <span className="tagpin__dot" aria-hidden="true" />
                      <span className="tagpin__label">
                        <button
                          type="button"
                          className="tagpin__name"
                          data-pin={p.personId}
                          aria-label={t.tagPin(p.displayName)}
                          aria-pressed={isSel}
                          onKeyDown={(e) => onPinKey(e, p)}
                          onClick={(e) => {
                            // A pointer press is handled on pointer-up (it may be a drag).
                            if (e.detail === 0) setSelected((s) => (s === p.personId ? null : p.personId))
                          }}
                        >
                          {p.displayName}
                        </button>
                        {isSel && (
                          <button
                            type="button"
                            className="tagpin__x"
                            aria-label={t.tagRemove(p.displayName)}
                            onClick={() => remove(p.personId)}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    </div>
                  )
                })}
                {pending && (
                  <>
                    <div
                      className="tagpin tagpin--pending"
                      style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
                      aria-hidden="true"
                    >
                      <span className="tagpin__dot" />
                    </div>
                    <div
                      // A new spot is a new question: remount, so the box takes focus again.
                      key={`${pending.x},${pending.y}`}
                      className={`tagpop ${popPlace(pending.x, pending.y)}`}
                      style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
                    >
                      <p className="tagpop__title">{t.tagWho}</p>
                      <PersonSearch
                        exclude={exclude}
                        onPick={(p) => add(p, pending)}
                        onEscape={() => setPending(null)}
                        label={t.tagWho}
                        placeholder={t.tagSearch}
                        autoFocus
                      />
                      <button type="button" className="btn btn--link btn--xs tagpop__cancel" onClick={() => setPending(null)}>
                        {t.cancel}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <aside className="tageditor__side">
          <h3 className="tageditor__h">{t.tagInPhoto(tags.length)}</h3>
          {tags.length === 0 ? (
            <p className="tageditor__empty">{t.tagNoneYet}</p>
          ) : (
            <ul className="tageditor__list">
              {tags.map((p) => {
                const unplaced = p.x === null || p.y === null
                return (
                  <li
                    key={p.personId}
                    className={`tageditor__row${hover === p.personId || selected === p.personId ? ' is-hover' : ''}${placing === p.personId ? ' is-placing' : ''}`}
                    onMouseEnter={() => setHover(p.personId)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(p.personId)}
                    onBlur={() => setHover(null)}
                  >
                    <span className="tageditor__av" aria-hidden="true">{initialsOf(p.displayName)}</span>
                    <span className="tageditor__who">
                      <span className="tageditor__name">{p.displayName}</span>
                      {unplaced && <span className="tageditor__sub">{t.tagNotPlaced}</span>}
                    </span>
                    <button
                      type="button"
                      className="btn btn--link btn--xs"
                      aria-pressed={placing === p.personId}
                      onClick={(e) => startPlacing(p.personId, e.detail === 0)}
                    >
                      {unplaced ? t.tagPlace : t.tagMove}
                    </button>
                    <button
                      type="button"
                      className="tageditor__rm"
                      aria-label={t.tagRemove(p.displayName)}
                      onClick={() => remove(p.personId)}
                    >
                      ×
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {!full && (
            <div className="tageditor__byname">
              <p className="tageditor__sub">{t.tagAddByName}</p>
              <PersonSearch
                exclude={exclude}
                onPick={(p) => add(p, null)}
                label={t.tagAddByName}
                placeholder={t.tagSearch}
              />
            </div>
          )}
          <p className="tageditor__note">{t.tagNote}</p>
        </aside>

        <div className="tageditor__foot">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => onDone(tags)}>
            {t.tagDone}
          </button>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            {t.cancel}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

/** Which way a pin's label hangs, so it stays on the photo near an edge. */
export function pinPlace(x: number, y: number): string {
  const c: string[] = []
  if (y > 0.82) c.push('tagpin--up')
  if (x < 0.14) c.push('tagpin--start')
  else if (x > 0.86) c.push('tagpin--end')
  return c.join(' ')
}

function popPlace(x: number, y: number): string {
  const c: string[] = []
  if (y > 0.55) c.push('tagpop--up')
  if (x < 0.3) c.push('tagpop--start')
  else if (x > 0.7) c.push('tagpop--end')
  return c.join(' ')
}

/**
 * The people search the tag editor uses twice: in "Who is this?" at a spot,
 * and in "Or add someone by name". The app's ONE person search
 * (`mentionable_people`); a name is only ever chosen from the list, never
 * matched from typed text (059). Enter takes the highlighted row and never
 * submits anything (David, 9 Oct: Enter in the old tag box published a
 * half-tagged post).
 */
export function PersonSearch({
  exclude,
  onPick,
  onEscape,
  label,
  placeholder,
  autoFocus,
}: {
  exclude: Set<string>
  onPick: (p: MentionablePerson) => void
  onEscape?: () => void
  label: string
  placeholder: string
  autoFocus?: boolean
}) {
  const t = COMPOSE[useHubLang()]
  const inputId = useId()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<MentionablePerson[]>([])
  const [total, setTotal] = useState(0)
  const [searching, setSearching] = useState(false)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setFound([])
      setTotal(0)
      setSearching(false)
      return
    }
    let live = true
    setSearching(true)
    const timer = window.setTimeout(() => {
      searchMentionable(q)
        .then((page) => {
          if (!live) return
          setFound(page.people)
          setTotal(page.total)
        })
        .catch(() => {
          if (!live) return
          setFound([])
          setTotal(0)
        })
        .finally(() => live && setSearching(false))
    }, 180)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [query])

  const offered = found.filter((p) => !exclude.has(p.personId))
  const hi = Math.min(active, Math.max(offered.length - 1, 0))

  useEffect(() => {
    setActive(0)
  }, [found])

  function take(p: MentionablePerson) {
    onPick(p)
    setQuery('')
    setActive(0)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (offered.length > 0) take(offered[hi])
    } else if (e.key === 'ArrowDown' && offered.length > 0) {
      e.preventDefault()
      setActive((hi + 1) % offered.length)
    } else if (e.key === 'ArrowUp' && offered.length > 0) {
      e.preventDefault()
      setActive((hi - 1 + offered.length) % offered.length)
    } else if (e.key === 'Escape') {
      // Clear the search first; then whatever holds it; never the composer.
      e.preventDefault()
      e.stopPropagation()
      if (query) setQuery('')
      else onEscape?.()
    }
  }

  const q = query.trim()
  return (
    <div className="personsearch">
      <label className="visually-hidden" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className="personsearch__input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={offered.length > 0}
        aria-controls={listId}
        aria-activedescendant={offered.length > 0 ? `${listId}-${hi}` : undefined}
      />
      {offered.length > 0 && (
        <ul className="personsearch__list" id={listId} role="listbox">
          {offered.map((p, i) => (
            <li key={p.personId} id={`${listId}-${i}`} role="option" aria-selected={i === hi}>
              <button
                type="button"
                className={`personsearch__opt${i === hi ? ' is-active' : ''}`}
                tabIndex={-1}
                // Keep focus in the box, so the list does not vanish on press.
                onPointerDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => take(p)}
              >
                <span className="personsearch__av" aria-hidden="true">{initialsOf(p.displayName)}</span>
                <span>{p.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {q.length >= 2 && !searching && found.length === 0 && <p className="personsearch__hint">{t.tagNobody}</p>}
      {q.length >= 2 && found.length > 0 && <PickerFoot shown={found.length} total={total} hasQuery />}
    </div>
  )
}

/**
 * 161. The pins in the photo viewer: read-only, names shown when the viewer's
 * toggle is on or when that name is hovered or focused in the line below.
 * aria-hidden, because the line below already lists the names in words.
 */
export function PhotoPins({
  tags,
  show,
  focus,
  suggested = [],
}: {
  tags: { personId: string; displayName: string; x: number | null; y: number | null }[]
  show: boolean
  focus: string | null
  /** 163. Waiting suggestions the viewer may see, drawn dashed with a "?". */
  suggested?: { id: string; personId: string; displayName: string; x: number | null; y: number | null }[]
}) {
  const t = COMPOSE[useHubLang()]
  const placed = tags.filter((p) => p.x !== null && p.y !== null)
  const maybe = suggested.filter((p) => p.x !== null && p.y !== null)
  if (placed.length === 0 && maybe.length === 0) return null
  return (
    <div className="tagpins" aria-hidden="true">
      {maybe.map((p) => (
        <div
          key={p.id}
          className={`tagpin tagpin--view tagpin--suggested ${pinPlace(p.x as number, p.y as number)}${show || focus === p.id ? ' is-shown' : ''}${focus === p.id ? ' is-hover' : ''}`}
          style={{ left: `${(p.x as number) * 100}%`, top: `${(p.y as number) * 100}%` }}
        >
          <span className="tagpin__dot" />
          <span className="tagpin__label">
            <span className="tagpin__name">{t.suggestedPin(p.displayName)}</span>
          </span>
        </div>
      ))}
      {placed.map((p) => {
        const on = show || focus === p.personId
        return (
          <div
            key={p.personId}
            className={`tagpin tagpin--view ${pinPlace(p.x as number, p.y as number)}${on ? ' is-shown' : ''}${focus === p.personId ? ' is-hover' : ''}`}
            style={{ left: `${(p.x as number) * 100}%`, top: `${(p.y as number) * 100}%` }}
          >
            <span className="tagpin__dot" />
            <span className="tagpin__label">
              <span className="tagpin__name">{p.displayName}</span>
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * 163. Suggest who is in a colleague's photo.
 *
 * The same photo-first shape as the editor, but every pick is sent at once
 * (suggest_photo_tag) and nothing is tagged: the person who posted it, a
 * moderator or a gallery admin decides, and the person suggested hears
 * nothing until then. "That's me" sends yourself (the server reads null as
 * the caller), at the spot you tapped or with no spot.
 */
export function SuggestTagDialog({
  path,
  src,
  width,
  height,
  tags,
  suggestions,
  onChanged,
  onClose,
}: {
  path: string
  src: string | null
  width: number
  height: number
  tags: { personId: string; displayName: string; x: number | null; y: number | null }[]
  /** The waiting suggestions this person may see (their own, or all if they decide). */
  suggestions: TagSuggestion[]
  onChanged: () => void
  onClose: () => void
}) {
  const t = COMPOSE[useHubLang()]
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string[]>([])
  const [failed, setFailed] = useState(false)
  const layerRef = useRef<HTMLDivElement>(null)
  const exclude = new Set([...tags.map((p) => p.personId), ...suggestions.map((p) => p.personId)])

  function pointFrom(e: { clientX: number; clientY: number }) {
    const r = layerRef.current?.getBoundingClientRect()
    if (!r || r.width === 0 || r.height === 0) return { x: 0.5, y: 0.5 }
    return { x: spot((e.clientX - r.left) / r.width), y: spot((e.clientY - r.top) / r.height) }
  }

  async function send(person: MentionablePerson | null, at: { x: number; y: number } | null) {
    setBusy(true)
    try {
      const out = await suggestPhotoTag(path, person?.personId ?? null, at)
      setSaid((cur) => [t.suggestResult(out, person?.displayName ?? null), ...cur].slice(0, 6))
      setPending(null)
      if (out === 'suggested') onChanged()
    } catch {
      setSaid((cur) => [t.tagFailed, ...cur].slice(0, 6))
    } finally {
      setBusy(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && pending) {
      e.preventDefault()
      e.stopPropagation()
      setPending(null)
    }
  }

  return (
    <Dialog open title={t.suggestTitle} onClose={onClose} className="tageditor">
      <div className="tageditor__body" onKeyDown={onKeyDown}>
        <div className="tageditor__main">
          <p className="tageditor__hint">{t.suggestHint}</p>
          <div className="tageditor__stage">
            <div className="tageditor__frame">
              {src && !failed ? (
                <img className="tageditor__img" src={src} alt="" width={width} height={height} draggable={false} onError={() => setFailed(true)} />
              ) : (
                <div className="tageditor__holder" style={{ aspectRatio: `${width} / ${height}` }}>
                  {failed ? t.photoFailed : t.tagPhotoLoading}
                </div>
              )}
              <div
                ref={layerRef}
                className={`tageditor__layer${busy ? ' is-full' : ''}`}
                onClick={(e) => {
                  if (e.target !== e.currentTarget || busy) return
                  setPending(pointFrom(e))
                }}
              >
                <PhotoPins tags={tags} show focus={null} suggested={suggestions} />
                {pending && (
                  <>
                    <div className="tagpin tagpin--pending" style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }} aria-hidden="true">
                      <span className="tagpin__dot" />
                    </div>
                    <div
                      key={`${pending.x},${pending.y}`}
                      className={`tagpop ${popPlace(pending.x, pending.y)}`}
                      style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
                    >
                      <p className="tagpop__title">{t.tagWho}</p>
                      <button type="button" className="btn btn--quiet btn--xs" disabled={busy} onClick={() => void send(null, pending)}>
                        {t.thatsMeHere}
                      </button>
                      <PersonSearch
                        exclude={exclude}
                        onPick={(p) => void send(p, pending)}
                        onEscape={() => setPending(null)}
                        label={t.tagWho}
                        placeholder={t.tagSearch}
                        autoFocus
                      />
                      <button type="button" className="btn btn--link btn--xs tagpop__cancel" onClick={() => setPending(null)}>
                        {t.cancel}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <aside className="tageditor__side">
          <button type="button" className="btn btn--quiet btn--sm tageditor__me" disabled={busy} onClick={() => void send(null, null)}>
            {t.thatsMe}
          </button>
          <div className="tageditor__byname">
            <p className="tageditor__sub">{t.suggestByName}</p>
            <PersonSearch exclude={exclude} onPick={(p) => void send(p, null)} label={t.suggestByName} placeholder={t.tagSearch} />
          </div>
          {said.length > 0 && (
            <ul className="tageditor__said" role="status">
              {said.map((line, i) => (
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          )}
        </aside>

        <div className="tageditor__foot">
          <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
            {t.tagDone}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
